import {
  ArrowRight,
  Bookmark,
  Eye,
  Inbox,
  Layers3,
  LockKeyhole,
  MessageSquareText,
  Send,
  ShieldCheck,
} from "lucide-react";
import type { ComponentType, PropsWithChildren } from "react";
import { Link, type MetaFunction } from "react-router";

import { LogoLockup } from "@/site/ui/logo";
import { ThemeControl } from "@/theme/theme-control";
import { Button } from "@/ui/button";

export const meta: MetaFunction = () => [
  { title: "Bookmarked FYI — turn saved X posts into collections" },
  {
    name: "description",
    content:
      "Save public X posts privately, add your point of view, and publish deliberate collections under your verified handle.",
  },
];

const exampleCollections = [
  {
    accent: "cobalt",
    count: 12,
    description: "Interfaces that make complex systems feel obvious.",
    title: "Design systems",
  },
  {
    accent: "coral",
    count: 8,
    description: "Clear explanations of agents, memory, and evaluation.",
    title: "Practical AI",
  },
  {
    accent: "fern",
    count: 17,
    description: "Small observations about building products in public.",
    title: "Maker notes",
  },
] as const;

// Position Bookmarked as a private capture queue that becomes public only
// through deliberate editorial collections.
//
export default function MarketingIndex() {
  return (
    <div className="min-h-dvh overflow-hidden">
      <div
        aria-hidden="true"
        className="bookmark-grid pointer-events-none absolute inset-x-0 top-0 -z-20 h-[54rem] opacity-80 [mask-image:linear-gradient(to_bottom,black,transparent)]"
      />

      <header className="bg-background/85 sticky top-0 z-50 border-b backdrop-blur-xl">
        <div className="mx-auto flex h-17 w-[calc(100%-2rem)] max-w-7xl items-center justify-between sm:w-[calc(100%-3rem)]">
          <LogoLockup linkTo="/" />
          <nav
            aria-label="Main navigation"
            className="text-muted-foreground hidden items-center gap-7 text-sm md:flex"
          >
            <Link className="hover:text-foreground" to="/pricing">
              Pricing
            </Link>
            <a className="hover:text-foreground" href="#collections">
              Collections
            </a>
            <a className="hover:text-foreground" href="#workflow">
              How it works
            </a>
            <a className="hover:text-foreground" href="#privacy">
              Privacy
            </a>
          </nav>
          <div className="flex items-center gap-1.5">
            <ThemeControl />
            <Button asChild className="rounded-full px-4" size="sm">
              <Link to="/studio">
                Open Studio <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-14 pt-16 pb-20 sm:w-[calc(100%-3rem)] sm:pt-24 sm:pb-28 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-20">
          <div>
            <p className="border-signal/30 bg-signal/10 text-signal-strong inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold tracking-[0.08em] uppercase">
              <Bookmark className="size-3.5" /> What you found, curated
            </p>
            <h1 className="font-display mt-7 max-w-2xl text-[3.6rem] leading-[0.92] font-semibold tracking-[-0.065em] text-balance sm:text-7xl lg:text-[5.2rem]">
              Saved posts deserve a point of view.
            </h1>
            <p className="text-muted-foreground mt-7 max-w-xl text-lg leading-8 sm:text-xl">
              Capture public X posts privately. Group the useful ones, add why
              they matter, and publish collections people can actually follow.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                className="h-12 rounded-full px-6 text-base"
                size="lg"
              >
                <Link to="/studio">
                  Start your library <ArrowRight />
                </Link>
              </Button>
              <Button
                asChild
                className="h-12 rounded-full px-6 text-base"
                size="lg"
                variant="outline"
              >
                <Link to="/pricing">View pricing</Link>
              </Button>
            </div>
            <div className="text-muted-foreground mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="text-success size-4" /> Verified X
                handle
              </span>
              <span>Private by default</span>
              <span>No automatic bookmark import</span>
            </div>
          </div>

          <div className="relative">
            <div className="bg-card rotate-1 rounded-[2.25rem] border p-5 shadow-2xl sm:p-7">
              <div className="flex items-center justify-between border-b pb-5">
                <div>
                  <p className="text-sm font-semibold">@mira’s library</p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    3 public collections
                  </p>
                </div>
                <span className="bg-signal/15 text-signal-strong rounded-full px-3 py-1.5 text-xs font-bold">
                  CURATED
                </span>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {exampleCollections.slice(0, 2).map((collection) => (
                  <ExampleCollection key={collection.title} {...collection} />
                ))}
              </div>
              <div className="bg-muted mt-4 flex items-center gap-3 rounded-2xl border border-dashed p-4">
                <span className="bg-card flex size-10 items-center justify-center rounded-xl border">
                  <Inbox className="text-signal-strong size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">Inbox</p>
                  <p className="text-muted-foreground text-xs">
                    6 private posts waiting for context
                  </p>
                </div>
                <LockKeyhole className="text-muted-foreground size-4" />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y">
          <div className="text-muted-foreground mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-5 py-5 text-sm sm:w-[calc(100%-3rem)] sm:grid-cols-3">
            <PromiseLine icon={Inbox}>Capture without publishing</PromiseLine>
            <PromiseLine icon={MessageSquareText}>
              Add your point of view
            </PromiseLine>
            <PromiseLine icon={Send}>Publish only what is ready</PromiseLine>
          </div>
        </section>

        <section
          className="mx-auto w-[calc(100%-2rem)] max-w-7xl py-24 sm:w-[calc(100%-3rem)] sm:py-32"
          id="collections"
        >
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="text-signal-strong text-xs font-bold tracking-[0.14em] uppercase">
                Lists with a thesis
              </p>
              <h2 className="font-display mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
                A folder says where. A collection says why.
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md leading-7">
              Give every collection a title, introduction, sequence, and your
              own notes while each original post stays canonical on X.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {exampleCollections.map((collection) => (
              <ExampleCollection key={collection.title} large {...collection} />
            ))}
          </div>
        </section>

        <section className="bg-foreground text-background" id="workflow">
          <div className="mx-auto w-[calc(100%-2rem)] max-w-7xl py-24 sm:w-[calc(100%-3rem)] sm:py-32">
            <p className="text-signal text-xs font-bold tracking-[0.14em] uppercase">
              Capture → curate → publish
            </p>
            <h2 className="font-display mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
              Keep the saving habit. Lose the bookmark graveyard.
            </h2>
            <div className="mt-14 grid border-y border-white/15 md:grid-cols-3">
              <ProcessStep icon={Bookmark} number="01" title="Save the link">
                Paste any public X status URL. It lands in your private Inbox or
                directly in the collection you are shaping.
              </ProcessStep>
              <ProcessStep
                icon={Layers3}
                number="02"
                title="Make the connection"
              >
                Add a useful title and a short curator note, then arrange posts
                in the order that makes your argument clear.
              </ProcessStep>
              <ProcessStep icon={Eye} number="03" title="Publish deliberately">
                A collection remains private until you publish it under your
                verified bookmarked.fyi/handle profile.
              </ProcessStep>
            </div>
          </div>
        </section>

        <section
          className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-12 py-24 sm:w-[calc(100%-3rem)] sm:py-32 lg:grid-cols-[0.75fr_1.25fr] lg:items-center lg:gap-24"
          id="privacy"
        >
          <div className="bookmark-paper flex aspect-square w-full max-w-sm items-center justify-center rounded-[2.5rem] border shadow-xl">
            <div className="bg-card flex size-40 flex-col items-center justify-center rounded-full border text-center shadow-lg">
              <LockKeyhole className="text-signal-strong size-7" />
              <span className="font-display mt-3 text-xl font-semibold">
                private first
              </span>
              <span className="text-muted-foreground mt-1 text-xs">
                always explicit
              </span>
            </div>
          </div>
          <div>
            <p className="text-signal-strong text-xs font-bold tracking-[0.14em] uppercase">
              The Bookmarked FYI principle
            </p>
            <h2 className="font-display mt-4 text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
              Your Inbox is yours. Your collections are intentional.
            </h2>
            <p className="text-muted-foreground mt-7 max-w-2xl text-lg leading-8">
              Sign-in verifies your public identity; it does not import your X
              bookmarks. Only links you paste are stored, and only collections
              you publish appear on your profile.
            </p>
            <Button
              asChild
              className="mt-9 h-12 rounded-full px-6 text-base"
              size="lg"
            >
              <Link to="/studio">
                Save your first post <ArrowRight />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t">
        <div className="text-muted-foreground mx-auto flex w-[calc(100%-2rem)] max-w-7xl flex-col gap-4 py-8 text-sm sm:w-[calc(100%-3rem)] sm:flex-row sm:items-center sm:justify-between">
          <LogoLockup className="text-foreground" linkTo="/" />
          <nav aria-label="Legal" className="flex gap-5">
            <Link className="hover:text-foreground" to="/pricing">
              Pricing
            </Link>
            <Link className="hover:text-foreground" to="/terms">
              Terms
            </Link>
            <Link className="hover:text-foreground" to="/privacy">
              Privacy
            </Link>
          </nav>
          <p>© {new Date().getFullYear()} Bookmarked FYI</p>
        </div>
      </footer>
    </div>
  );
}

// Show one static product example without loading third-party X content.
//
function ExampleCollection({
  accent,
  count,
  description,
  large = false,
  title,
}: {
  accent: string;
  count: number;
  description: string;
  large?: boolean;
  title: string;
}) {
  return (
    <article
      data-accent={accent}
      className={
        "bookmark-collection relative overflow-hidden rounded-[1.7rem] border p-5 " +
        (large ? "min-h-72 p-7" : "min-h-48")
      }
    >
      <div className="relative flex h-full flex-col">
        <div className="flex items-center justify-between text-[0.65rem] font-bold tracking-[0.1em] uppercase opacity-60">
          <span>{count} posts</span>
          <Bookmark className="size-3.5" />
        </div>
        <h3 className="font-display mt-7 text-2xl font-semibold tracking-[-0.04em]">
          {title}
        </h3>
        <p className="mt-3 text-sm leading-6 opacity-70">{description}</p>
        {large && (
          <span className="mt-auto flex items-center gap-2 pt-8 text-sm font-semibold">
            Explore <ArrowRight className="size-4" />
          </span>
        )}
      </div>
    </article>
  );
}

interface IconProps {
  icon: ComponentType<{ className?: string }>;
}

// Keep the three product promises scannable below the hero.
//
function PromiseLine({ children, icon: Icon }: PropsWithChildren<IconProps>) {
  return (
    <p className="flex items-center gap-2.5">
      <Icon className="text-signal-strong size-4" /> {children}
    </p>
  );
}

// Explain one stage of the curation workflow in the dark narrative section.
//
function ProcessStep({
  children,
  icon: Icon,
  number,
  title,
}: PropsWithChildren<IconProps & { number: string; title: string }>) {
  return (
    <article className="border-white/15 py-8 md:border-r md:px-8 md:first:pl-0 md:last:border-r-0 md:last:pr-0">
      <div className="flex items-center justify-between">
        <Icon className="text-signal size-5" />
        <span className="text-xs font-bold opacity-40">{number}</span>
      </div>
      <h3 className="font-display mt-8 text-2xl font-semibold">{title}</h3>
      <p className="mt-4 text-sm leading-7 opacity-65">{children}</p>
    </article>
  );
}
